import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Filter, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { productsAPI } from '../services/api';
import { categories } from '../mock/mockData';

const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const categoryParam = searchParams.get('category') || 'all';
    setSelectedCategory(categoryParam);
    loadProducts(categoryParam);
  }, [searchParams]);

  useEffect(() => {
    // Re-sort when sortBy changes
    sortProducts();
  }, [sortBy]);

  const loadProducts = async (category) => {
    setLoading(true);
    try {
      const data = await productsAPI.getAll(category === 'all' ? null : category);
      setFilteredProducts(data);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortProducts = () => {
    const sorted = [...filteredProducts];
    if (sortBy === 'price-low') {
      sorted.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      sorted.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'name') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
    setFilteredProducts(sorted);
  };

  const handleCategoryChange = (categorySlug) => {
    setSelectedCategory(categorySlug);
    if (categorySlug === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: categorySlug });
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12 lg:py-16">
        {/* Header */}
        <div className="mb-12 lg:mb-16">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-6">
            COLLECTION
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'item' : 'items'}
          </p>
        </div>

        {/* Filters & Sort */}
        <div className="mb-12 flex flex-col lg:flex-row justify-between gap-6">
          {/* Categories - Desktop */}
          <div className="hidden lg:flex gap-6 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`text-sm font-bold tracking-wider pb-2 border-b-2 transition-colors ${
                  selectedCategory === cat.slug
                    ? 'border-black'
                    : 'border-transparent hover:border-gray-300'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden flex items-center gap-2 text-sm font-bold tracking-wider"
          >
            <Filter size={18} strokeWidth={2.5} />
            FILTERS
          </button>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm font-bold tracking-wider border-b-2 border-black pb-2 bg-white focus:outline-none cursor-pointer"
          >
            <option value="featured">FEATURED</option>
            <option value="price-low">PRICE: LOW TO HIGH</option>
            <option value="price-high">PRICE: HIGH TO LOW</option>
            <option value="name">NAME: A-Z</option>
          </select>
        </div>

        {/* Mobile Filters */}
        {showFilters && (
          <div className="lg:hidden fixed inset-0 z-50 bg-white">
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-black tracking-tighter">FILTERS</h2>
                <button onClick={() => setShowFilters(false)}>
                  <X size={24} strokeWidth={2.5} />
                </button>
              </div>

              <div className="space-y-4">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      handleCategoryChange(cat.slug);
                      setShowFilters(false);
                    }}
                    className={`block w-full text-left text-xl font-bold tracking-tighter py-3 ${
                      selectedCategory === cat.slug ? 'opacity-100' : 'opacity-40'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:gap-12">
            {filteredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <p className="text-2xl font-bold tracking-wider text-gray-400">
              NO ITEMS FOUND
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Catalog;
